// MobX store to hold data for UI changes and updates globally
import { makeObservable, observable, action, computed, autorun } from 'mobx';
import Cookies from 'js-cookie';
import salestax from '../utils/calculator/salestax';
import { getCardPriceDesc } from '../utils/commonUtils';

export class AppUiStore {
  currentBnOStep = 'model';
  prevBnOStep = 'model';
  showLocationPopup = Cookies.get('province') ? false : true;
  currentProvince = Cookies.get('province') || '';
  currentProvinceLabel = Cookies.get('provinceLabel') || '';
  currentPostalCode = Cookies.get('postalCode') || '';
  currentLanguage = Cookies.get('genesis#lang') || '';
  redirectedOnce = false;
  preventItemClick = false;
  visitedModelOnce = false;
  visitedTrimOnce = false;
  allAccessoriesList = [];
  allAccessoriesCategories = [];
  paymentOptions = null;
  includeTax = false;
  openPaymentDrawer = false;
  isResumeBuild = false;
  triggerPaymentsAPI = 0;

  //User selections in the flow
  selectedModel = null;
  selectedTrim = null;
  extColorSelection = null;
  intColorSelection = null;
  selectedAccessories = [];
  selectedProtectionPlans = [];
  protectionPlanIdList = [];
  activeFilterModel = null;
  activeFilterTrims = null;
  activeFilterAccessories = null;
  activeFilterProtection = null;
  includeSalesTax = false;
  showDetailedPricing = false;
  selectedLeaseTerm = null;
  selectedFinanceTerm = null;
  financeDownpayment = 1000;
  leaseDownpayment = 1000;
  leaseKmAllowance = null;
  selectedDealerCode = null;
  dealerSelectionBAS = null;

  //API Response stored to avoid unnecessary calls to server
  modelsStepAPIData = null;
  trimsStepAPIData = null;
  colorsStepAPIData = null;
  accessoriesStepAPIData = null;
  protectionStepAPIData = null;
  popularAccessoriesAPIData = null;
  paymentServiceAPIData = null;
  structuredProtectionCardsData = null;

  //Config data used for Resume Build functionality
  configData = {
    configId: null, //SP
    step: null,
    recordType: null, //SP
    language: null, //SP
    websiteURL: null,
    recipientFirstName: null, //SP
    recipientLastName: null, //SP
    recipientEmailAddress: null, //SP
    extTrimid: 0,
    googleRecaptchaResponse: null, //SP
    selectedAccessories: [],
    lastSavedProgress: null, //SP
    progressData: {
      paymentFrequency: 'monthly',
      latestModelYear: null,
      includeSalesTax: false,
      paymentType: null,
      extModelId: 0,
      bodyType_En: null,
      modelName: null,
      modelImagePath: null,
      modelYear: null,
      extTrimId: 0,
      trimName: null,
      exteriorSapCode: null,
      interiorSapCode: null,
      hsc: null,
      provinceCode: null,
      postalCode: Cookies.get('postalCode') || '',
      accessoryProductSkus: [],
      protectionPlanIds: [],
      selectedDealerCode: null,
      finance: {
        selectedTerm: 0,
        downPayment: 0,
        estimatedAnnualKm: 0,
        termUnit: null,
        rate: 0,
      },
      lease: {
        selectedTerm: 0,
        downPayment: 0,
        estimatedAnnualKm: 0,
        termUnit: null,
        rate: 0,
      },
    },
  };

  //Payload for price API
  priceApiPayload = {
    modelName: null,
    modelYear: null,
    hsc: null,
    extSapCode: null,
    intSapCode: null,
    paymentType: 'all',
    financeDownPayment: null,
    leaseDownPayment: null,
    financePaymentTerm: null,
    leasePaymentTerm: null,
    annualkm: null,
    includesaletax: true,
    accessories: [],
    protectionPlans: [],
    paymentFrequency: 'monthly',
    language: Cookies.get('genesis#lang'),
    dealerCode: null,
    province: Cookies.get('province'),
  };

  constructor() {
    makeObservable(this, {
      // OBSERVABLES --------------------------------------------------------------
      currentBnOStep: observable,
      prevBnOStep: observable,
      currentProvince: observable,
      currentProvinceLabel: observable,
      currentPostalCode: observable,
      showLocationPopup: observable,
      currentLanguage: observable,
      redirectedOnce: observable,
      preventItemClick: observable,
      visitedModelOnce: observable,
      visitedTrimOnce: observable,
      allAccessoriesList: observable,
      allAccessoriesCategories: observable,
      paymentOptions: observable,
      includeTax: observable,
      openPaymentDrawer: observable,
      isResumeBuild: observable,
      triggerPaymentsAPI: observable,

      //User selections in the flow - Observables
      selectedModel: observable,
      selectedTrim: observable,
      extColorSelection: observable,
      intColorSelection: observable,
      selectedAccessories: observable,
      selectedProtectionPlans: observable,
      protectionPlanIdList: observable,
      activeFilterModel: observable,
      activeFilterTrims: observable,
      activeFilterAccessories: observable,
      activeFilterProtection: observable,
      includeSalesTax: observable,
      showDetailedPricing: observable,
      selectedLeaseTerm: observable,
      selectedFinanceTerm: observable,
      financeDownpayment: observable,
      leaseDownpayment: observable,
      leaseKmAllowance: observable,
      selectedDealerCode: observable,
      dealerSelectionBAS: observable,

      //API Response stored to avoid unnecessary calls to server - Observables
      modelsStepAPIData: observable,
      trimsStepAPIData: observable,
      colorsStepAPIData: observable,
      accessoriesStepAPIData: observable,
      protectionStepAPIData: observable,
      popularAccessoriesAPIData: observable,
      paymentServiceAPIData: observable,
      structuredProtectionCardsData: observable,

      //Config data used for Resume Build functionality - Observables
      configData: observable,
      priceApiPayload: observable,

      // ACTIONS --------------------------------------------------------------
      updateLocationStored: action,
      toogleLocationPopup: action,
      updateBnOStep: action,
      updateBnOPrevStep: action,
      updateRedirectedOnce: action,
      updatePreventItemClick: action,
      updateConfigData: action,
      updateAllConfig: action,
      updateVisitedModelOnce: action,
      updateVisitedTrimOnce: action,
      resetStore: action,
      resetStoreRedirected: action,
      updateAllAccessoriesList: action,
      updateAllAccessoriesCategories: action,
      updateIncludeTax: action,
      updatePaymentAPIRes: action,
      setOpenPaymentDrawer: action,
      updateIsResumeBuild: action,
      updatePriceApiPayload: action,
      updatePartialPriceApiPayload: action,
      updatePriceApiPayloadProtection: action,
      resetPriceApiPayload: action,
      updateTriggerPaymentsAPI: action,
      resetTriggerPaymentsAPI: action,

      //User selections in the flow - Actions
      updateSelectedModel: action,
      updateSelectedModelYear: action,
      updateSelectedTrim: action,
      updateExtColorSelection: action,
      updateIntColorSelection: action,
      updateSelectedAccessories: action,
      updateSelectedProtectionPlans: action,
      updateSalesTaxIncluded: action,
      updateShowDetailedPricing: action,
      addToProtectionPlanIdList: action,
      updateAllProtectionPlanIdList: action,
      updateProtectionPlanIdList: action,
      removeProtectionPlanIdList: action,
      removeAccessoryFromList: action,
      updateActiveFilterModel: action,
      updateActiveFilterTrims: action,
      updateActiveFilterAccessories: action,
      updateActiveFilterProtection: action,
      updateSelectedLeaseTerm: action,
      updateSelectedFinanceTerm: action,
      updateFinanceDownpayment: action,
      updateLeaseDownpayment: action,
      updateLeaseKmAllowance: action,
      updateSelectedDealerCode: action,
      updateDealerSelectionBAS: action,

      //API Response stored to avoid unnecessary calls to server - Actions
      updateModelsStepAPIData: action,
      updateTrimsStepAPIData: action,
      updateColorsStepAPIData: action,
      updateAccessoriesStepAPIData: action,
      updateProtectionStepAPIData: action,
      updatePopularAccessoriesAPIData: action,
      updatePaymentServiceAPIData: action,
      updatePartialPaymentServiceAPIData: action,
      updateStructuredProtectionCardsData: action,

      // COMPUTED
      countMultiplied: computed,
    });
  }

  updateBnOStep = (stepTo) => {
    this.currentBnOStep = stepTo;
  };

  updateBnOPrevStep = () => {
    this.prevBnOStep = this.currentBnOStep;
  };

  updateIsResumeBuild = (flag) => {
    this.isResumeBuild = flag;
  };

  updateLocationStored = (postalDetails) => {
    this.currentProvince = postalDetails.provinceCode;
    this.currentProvinceLabel = postalDetails.provinceName;
    this.currentPostalCode = postalDetails.postalCodeValue;
  };

  toogleLocationPopup = () => {
    this.showLocationPopup = !this.showLocationPopup;
  };

  updateModelsStepAPIData = (responseObj) => {
    this.modelsStepAPIData = responseObj;
  };

  updateSelectedModel = (model) => {
    this.selectedModel = model;
  };

  updateSelectedTrim = (trim) => {
    this.selectedTrim = trim;
  };

  updateSelectedAccessories = (accessories) => {
    this.selectedAccessories = accessories;
  };

  updateRedirectedOnce = () => {
    this.redirectedOnce = true;
  };

  updateActiveFilterModel = (modelFilter) => {
    this.activeFilterModel = modelFilter;
  };

  updateActiveFilterTrims = (trimsFilter) => {
    this.activeFilterTrims = trimsFilter;
  };

  updateActiveFilterAccessories = (accessoriesFilter) => {
    this.activeFilterAccessories = accessoriesFilter;
  };

  updatePreventItemClick = (value) => {
    this.preventItemClick = value;
  };

  updateAllConfig = (configs) => {
    this.configData = configs;
  };

  updateDealerSelectionBAS = (dealer) => {
    this.dealerSelectionBAS = dealer;
  };

  updateAllProtectionPlanIdList = (list) => {
    this.protectionPlanIdList = list;
  };

  updateConfigData = (value, firstKey, secondKey, thirdKey, fourthKey) => {
    if (firstKey && secondKey && thirdKey && fourthKey) {
      this.configData[firstKey][secondKey][thirdKey][fourthKey] = value;
      return;
    }
    if (firstKey && secondKey && thirdKey) {
      this.configData[firstKey][secondKey][thirdKey] = value;
      return;
    }
    if (firstKey && secondKey) {
      this.configData[firstKey][secondKey] = value;
      return;
    }
    if (firstKey) {
      this.configData[firstKey] = value;
      return;
    }
  };

  updateExtColorSelection = (newColorSelected) => {
    this.extColorSelection = newColorSelected;
  };

  updateIntColorSelection = (newIntColor) => {
    this.intColorSelection = newIntColor;
  };

  updateActiveFilterProtection = (protectionFilter) => {
    this.activeFilterProtection = protectionFilter;
  };

  updateSelectedProtectionPlans = (plans) => {
    this.selectedProtectionPlans = plans;
  };

  updateVisitedModelOnce = (flag) => {
    this.visitedModelOnce = flag;
  };
  updateVisitedTrimOnce = (flag) => {
    this.visitedTrimOnce = flag;
  };

  resetStore = () => {
    this.currentBnOStep = 'model';
    this.prevBnOStep = 'model';
    this.showLocationPopup = Cookies.get('province') ? false : true;
    this.currentProvince = Cookies.get('province') || '';
    this.currentProvinceLabel = Cookies.get('provinceLabel') || '';
    this.currentPostalCode = Cookies.get('postalCode') || '';
    this.currentLanguage = Cookies.get('genesis#lang') || '';
    this.modelsStepAPIData = null;
    this.trimsStepAPIData = null;
    this.colorsStepAPIData = null;
    this.accessoriesStepAPIData = null;
    this.protectionStepAPIData = null;
    this.popularAccessoriesAPIData = null;
    this.paymentServiceAPIData = null;
    this.selectedModel = null;
    this.selectedTrim = null;
    this.selectedColorExt = null;
    this.selectedColorInt = null;
    this.selectedAccessories = [];
    this.redirectedOnce = false;
    this.activeFilterModel = this.activeFilterModel || null;
    this.activeFilterTrims = null;
    this.activeFilterAccessories = null;
    this.preventItemClick = false;
    this.extColorSelection = null;
    this.activeFilterProtection = null;
    this.selectedProtectionPlans = [];
    this.visitedModelOnce = false;
    this.visitedTrimOnce = false;
    this.paymentOptions = null;
    this.includeTax = false;
    this.selectedLeaseTerm = null;
    this.selectedFinanceTerm = null;
    this.financeDownpayment = 1000;
    this.leaseDownpayment = 1000;
    this.leaseKmAllowance = null;
    this.configData = {
      configId: null,
      step: null,
      recordType: null,
      language: null,
      websiteURL: null,
      recipientFirstName: null,
      recipientLastName: null,
      recipientEmailAddress: null,
      extTrimid: 0,
      googleRecaptchaResponse: null,
      selectedAccessories: [],
      progressData: {
        paymentFrequency: 'monthly',
        latestModelYear: this.configData.progressData.latestModelYear || null,
        includeSalesTax: false,
        paymentType: null,
        extModelId: 0,
        bodyType_En: null,
        modelName: null,
        modelImagePath: null,
        modelYear: null,
        extTrimId: 0,
        trimName: null,
        exteriorSapCode: null,
        interiorSapCode: null,
        hsc: null,
        provinceCode: null,
        postalCode: this.currentPostalCode,
        accessoryProductSkus: [],
        protectionPlanIds: [],
        selectedDealerCode: null,
        finance: {
          selectedTerm: 0,
          downPayment: 0,
          estimatedAnnualKm: 0,
          termUnit: null,
          rate: 0,
        },
        lease: {
          selectedTerm: 0,
          downPayment: 0,
          estimatedAnnualKm: 0,
          termUnit: null,
          rate: 0,
        },
      },
    };
  };

  resetStoreRedirected = () => {
    // this.currentBnOStep = 'model';
    this.prevBnOStep = 'model';
    this.showLocationPopup = Cookies.get('province') ? false : true;
    this.currentProvince = Cookies.get('province') || '';
    this.currentProvinceLabel = Cookies.get('provinceLabel') || '';
    this.currentPostalCode = Cookies.get('postalCode') || '';
    this.currentLanguage = Cookies.get('genesis#lang') || '';
    if (this.currentBnOStep !== 'trims') {
      this.modelsStepAPIData = null;
    }
    // this.trimsStepAPIData = null;
    this.colorsStepAPIData = null;
    this.accessoriesStepAPIData = null;
    this.protectionStepAPIData = null;
    this.popularAccessoriesAPIData = null;
    // this.paymentServiceAPIData = null;
    // this.selectedModel = null;
    // this.selectedTrim = null;
    this.selectedColorExt = null;
    this.selectedColorInt = null;
    this.selectedAccessories = [];
    this.redirectedOnce = false;
    this.activeFilterModel = this.activeFilterModel || null;
    this.activeFilterTrims = null;
    this.activeFilterAccessories = null;
    this.preventItemClick = false;
    this.extColorSelection = null;
    this.activeFilterProtection = null;
    this.selectedProtectionPlans = [];
    this.visitedModelOnce = false;
    this.visitedTrimOnce = false;
    this.paymentOptions = null;
    this.includeTax = false;
    // this.selectedLeaseTerm = null;
    // this.selectedFinanceTerm = null;
    this.financeDownpayment = 1000;
    this.leaseDownpayment = 1000;
    this.leaseKmAllowance = null;
    this.configData = {
      configId: null,
      step: null,
      recordType: null,
      language: null,
      websiteURL: null,
      recipientFirstName: null,
      recipientLastName: null,
      recipientEmailAddress: null,
      extTrimid: 0,
      googleRecaptchaResponse: null,
      selectedAccessories: [],
      progressData: {
        paymentFrequency: 'monthly',
        latestModelYear: this.configData.progressData.latestModelYear || null,
        includeSalesTax: false,
        paymentType: this.configData.progressData.paymentType || 'lease',
        extModelId: 0,
        bodyType_En: null,
        modelName: null,
        modelImagePath: null,
        modelYear: null,
        extTrimId: 0,
        trimName: null,
        exteriorSapCode: null,
        interiorSapCode: null,
        hsc: null,
        provinceCode: null,
        postalCode: this.currentPostalCode,
        accessoryProductSkus: [],
        protectionPlanIds: [],
        selectedDealerCode: null,
        finance: {
          selectedTerm: 0,
          downPayment: 0,
          estimatedAnnualKm: 0,
          termUnit: null,
          rate: 0,
        },
        lease: {
          selectedTerm: 0,
          downPayment: 0,
          estimatedAnnualKm: 0,
          termUnit: null,
          rate: 0,
        },
      },
    };
  };

  updatePriceApiPayload = (payload) => {
    this.priceApiPayload = payload;
  };

  updateTriggerPaymentsAPI = () => {
    this.triggerPaymentsAPI += 1;
  };

  resetTriggerPaymentsAPI = () => {
    this.triggerPaymentsAPI = 0;
  };

  updatePartialPriceApiPayload = (firstKey, value, clearList, updateSet) => {
    if (firstKey === 'accessories' || firstKey === 'protectionPlans') {
      if (clearList) {
        this.priceApiPayload[firstKey] = [];
      } else if (updateSet) {
        this.priceApiPayload[firstKey] = value;
      } else {
        this.priceApiPayload[firstKey].push(value);
      }
    } else {
      if (firstKey) {
        this.priceApiPayload[firstKey] = value;
        return;
      }
    }
  };

  updatePriceApiPayloadProtection = (planName, planCode, planCoverage, dropdownLabel, cardId) => {
    //update specific plan with updates
    this.priceApiPayload.protectionPlans.every((plan) => {
      if (
        plan.name === planName &&
        (dropdownLabel === 'Coverage Level' || dropdownLabel === 'Deductible')
      ) {
        plan.code = planCode;
        this.protectionPlanIdList.every((idList) => {
          let splitTerm = idList.split('|');
          let termMileage = splitTerm[splitTerm.length - 1];
          let term = termMileage.split('/')[0];
          let mileage = termMileage.split('/')[1] || null;
          if (term.includes('year')) {
            term = Number(term.split(' ')[0]) * 12 + ' months'; //Converting years to months
          }
          if (term.includes('-')) {
            if (term.split('-')[1].includes('48')) {
              term = '49';
            } else {
              term = '12';
            }
          }
          plan.term = term;
          plan.mileage = mileage;
          plan.code = planCode;
        });
        return false;
      } else if (plan.name === planName && dropdownLabel === 'Coverage Length') {
        let term = planCoverage.split('/')[0];
        let mileage = planCoverage.split('/')[1] || null;
        if (term.includes('year')) {
          term = Number(term.split(' ')[0]) * 12 + ' months'; //Converting years to months
        }
        if (term.includes('-')) {
          if (term.split('-')[1].includes('48')) {
            term = '49';
          } else {
            term = '12';
          }
        }
        plan.term = term;
        plan.mileage = mileage;
        plan.code = planCode;
      } else {
        return true;
      }
    });
  };

  resetPriceApiPayload = () => {
    this.priceApiPayload = {
      modelName: null,
      modelYear: null,
      hsc: null,
      extSapCode: null,
      intSapCode: null,
      paymentType: 'all',
      financeDownPayment: null,
      leaseDownPayment: null,
      financePaymentTerm: null,
      leasePaymentTerm: null,
      annualkm: null,
      includesaletax: true,
      accessories: [],
      protectionPlans: [],
      paymentFrequency: 'monthly',
      language: Cookies.get('genesis#lang'),
      dealerCode: null,
      province: Cookies.get('province'),
    };
  };

  updateProtectionPlanOnDropdown = (card) => {
    this.selectedProtectionPlans = this.selectedProtectionPlans.map((plan) => {
      if (card.id === plan.id) {
        return card;
      }
      return { ...plan };
    });
  };

  updateStructuredProtectionCardsData = (data) => {
    this.structuredProtectionCardsData = data;
  };

  updateAllAccessoriesList = (list) => {
    this.allAccessoriesList = list;
  };

  updateAllAccessoriesCategories = (list) => {
    this.allAccessoriesCategories = list;
  };

  updatePaymentOptions = (paymentObj) => {
    this.paymentOptions = paymentObj;
  };

  updateIncludeTax = (bool) => {
    this.includeTax = bool;
  };

  addToProtectionPlanIdList = (item) => {
    const index = this.protectionPlanIdList.findIndex((pp) => {
      return this.protectionPlanIdList.includes(item);
    });
    if (index === -1) {
      this.protectionPlanIdList.push(item);
    } else {
      this.protectionPlanIdList.splice(index, 1);
    }
    this.configData.progressData.protectionPlanIds = this.protectionPlanIdList;
  };

  updateProtectionPlanIdList = (item) => {
    const itemId = item.split('|')[0];
    this.protectionPlanIdList.every((plan, i) => {
      const planId = plan.split('|')[0];
      if (planId === itemId) {
        this.protectionPlanIdList[i] = item;
        return false;
      }
      return true;
    });
    this.configData.progressData.protectionPlanIds = this.protectionPlanIdList;
  };

  removeProtectionPlanIdList = (id) => {
    this.protectionPlanIdList = this.protectionPlanIdList.filter(
      (plan) => plan.split('|')[0] !== id
    );
    this.configData.progressData.protectionPlanIds = this.protectionPlanIdList;
  };

  removeAccessoryFromList = (id) => {
    let accessoryIdToRemove;
    this.selectedAccessories = this.selectedAccessories.filter((accessory) => {
      if (accessory.id !== id) {
        return true;
      } else {
        accessoryIdToRemove = accessory.id;
        return false;
      }
    });
    this.configData.progressData.accessoryProductSkus =
      this.configData.progressData.accessoryProductSkus.filter(
        (sku) => sku !== accessoryIdToRemove
      );
  };

  updatePaymentAPIRes = (response) => {
    this.paymentAPIRes = response;
  };

  get countMultiplied() {
    return this.count * 2;
  }

  getProtectionPlanPrice = (item) => {
    const { price } = getCardPriceDesc(item);
    return price;
  };

  getAddOns = () => {
    const addOns = {
      extColour: 0,
      intColour: 0,
      accessories: 0,
      protectionPlan: 0,
    };
    const addOnArray = [];
    addOns.extColour = this.extColorSelection?.price;
    addOns.intColour = this.intColorSelection?.price;

    this.selectedAccessories.forEach((item) => {
      addOns.accessories += Number(item.price) * 100;
    });

    addOns.accessories /= 100;

    this.selectedProtectionPlans.forEach((item) => {
      addOns.protectionPlan += this.getProtectionPlanPrice(item);
    });

    Object.keys(addOns).forEach((addon) => {
      addOnArray.push({ name: addon, value: addOns[addon] });
    });

    return addOnArray;
  };

  getAdditionalInfo = () => {
    const additionalInfo = {};
    if (this.currentProvince === 'BC') {
      const addOns = this.getAddOns();
      const msrpValue = this.selectedTrim.msrp;
      const addOnsTotalValue = addOns.reduce(function (a, b) {
        return a + b.value;
      }, 0);
      const amountForBC = msrpValue + addOnsTotalValue;
      const vehicleType = this.selectedTrim.isEv ? 'Electric' : '';
      additionalInfo['amountForBC'] = amountForBC;
      additionalInfo['vehicleType'] = vehicleType;
    }
    return additionalInfo;
  };

  computeCombinedTax = (price) => {
    const additionalInfo = this.getAdditionalInfo();
    const taxOptions = (amount) => ({
      mode: 'SIMPLE',
      province: this.currentProvince,
      amount,
      ...additionalInfo,
    });

    const luxuryTax = salestax.getLuxuryTax(price);
    const taxableAmt = price + luxuryTax;
    const commbinedTax = salestax.calculateTaxableAmount(taxOptions(taxableAmt)).salesTax;
    return commbinedTax;
  };

  calculateInstallment = (getPrice) => {
    const price = getPrice ? getPrice : 0;
    const additionalInfo = this.getAdditionalInfo();
    //
    const taxOptions = (amount) => ({
      mode: 'SIMPLE',
      province: this.currentProvince,
      amount,
      ...additionalInfo,
    });
    // conversion factor for the payment frequency
    const frequencyFactor = {
      monthly: 1,
      biweekly: 12 / 26,
      weekly: 12 / 52,
    };
    //get the term and rate of selected payment method
    let term = 60; //Default lease term
    let rate = 5.9; //Default lease rate
    if (this.configData?.progressData?.paymentType === 'lease' && this.selectedLeaseTerm) {
      term = this.selectedLeaseTerm.term;
      rate = this.selectedLeaseTerm.rate;
    } else if (
      this.configData?.progressData?.paymentType === 'finance' &&
      this.selectedFinanceTerm
    ) {
      term = this.selectedFinanceTerm.term;
      rate = this.selectedFinanceTerm.rate;
    }
    //
    const factor = Math.floor((rate / 100 / 24.0) * 1000000) / 1000000;

    let payment =
      (price / term + price * factor) *
      frequencyFactor[this.configData?.progressData?.paymentFrequency];

    if (this.includeTax) {
      payment = salestax.calc(taxOptions(payment)).salesTotal;
    }

    return payment;
  };

  setOpenPaymentDrawer = (flag) => {
    this.openPaymentDrawer = flag;
  };

  updateTrimsStepAPIData = (apiData) => {
    this.trimsStepAPIData = apiData;
  };
  updateColorsStepAPIData = (apiData) => {
    this.colorsStepAPIData = apiData;
  };
  updateAccessoriesStepAPIData = (apiData) => {
    this.accessoriesStepAPIData = apiData;
  };
  updateProtectionStepAPIData = (apiData) => {
    this.protectionStepAPIData = apiData;
  };
  updatePopularAccessoriesAPIData = (apiData) => {
    this.popularAccessoriesAPIData = apiData;
  };
  updatePaymentServiceAPIData = (apiData) => {
    this.paymentServiceAPIData = apiData;
  };

  updatePartialPaymentServiceAPIData = (data) => {
    this.paymentServiceAPIData = { ...this.paymentServiceAPIData, ...data };
  };

  updateSelectedModelYear = (year) => {
    this.selectedModel.modelYear = year;
  };

  updateSalesTaxIncluded = (flag) => {
    this.includeSalesTax = flag;
  };

  updateShowDetailedPricing = (flag) => {
    this.showDetailedPricing = flag;
  };

  updateSelectedLeaseTerm = (term) => {
    this.selectedLeaseTerm = term;
  };

  updateSelectedFinanceTerm = (term) => {
    this.selectedFinanceTerm = term;
  };

  updateFinanceDownpayment = (downpayment) => {
    this.financeDownpayment = downpayment;
  };

  updateLeaseDownpayment = (downpayment) => {
    this.leaseDownpayment = downpayment;
  };

  updateLeaseKmAllowance = (kmAllowance) => {
    this.leaseKmAllowance = kmAllowance;
  };

  updateSelectedDealerCode = (dealerCode) => {
    this.selectedDealerCode = dealerCode;
  };
}

export class UtilityUiStore {
  urlParams = new URLSearchParams(window.location.search);
  urlConfigId = this.urlParams.get('configId');
  showLocationPopup = this.urlConfigId || Cookies.get('province') ? false : true;
  currentProvince = Cookies.get('province') || '';
  currentProvinceLabel = Cookies.get('provinceLabel') || '';
  currentPostalCode = Cookies.get('postalCode') || '';

  constructor() {
    makeObservable(this, {
      // OBSERVABLES
      currentProvince: observable,
      currentProvinceLabel: observable,
      currentPostalCode: observable,
      showLocationPopup: observable,

      // ACTIONS
      updateLocationStored: action,
      toogleLocationPopup: action,
      updateLocationPopup: action,
      updateProvinceValues: action,

      // COMPUTED SAMPLE PROP
      countMultiplied: computed,
    });
  }

  updateLocationStored = (postalDetails) => {
    Cookies.set('province', postalDetails.provinceCode);
    Cookies.set('provinceLabel', postalDetails.provinceName);
    if (postalDetails.postalCodeValue) {
      Cookies.set('postalCode', postalDetails.postalCodeValue);
      this.currentPostalCode = postalDetails.postalCodeValue;
    }
    this.currentProvince = postalDetails.provinceCode;
    this.currentProvinceLabel = postalDetails.provinceName;
  };

  updateProvinceValues = (province, provinceLabel) => {
    this.currentProvince = province;
    this.currentProvinceLabel = provinceLabel;
  };

  toogleLocationPopup = () => {
    this.showLocationPopup = !this.showLocationPopup;
  };

  updateLocationPopup = (flag) => {
    this.showLocationPopup = flag;
  };

  get countMultiplied() {
    return this.count * 2;
  }
}
